// frontend/packages/business-console/src/pages/BlogAuthor.jsx
//
// AI-guided blog authoring (design: docs/design/ai-blog-authoring.md).
//
// A business admin describes the post they want; the embedded agent
// interviews briefly, then writes the whole post in the business's brand
// voice (server-built prompt + schema validation + per-tenant rate +
// spend caps, all enforced backend-side). The admin revises by chatting,
// saves the draft to the site's uat branch, previews it on the real
// (themed) draft site, then approves and publishes to production.
//
// Generation streams from /api/v2/blog-authoring; the draft write,
// status, and publish reuse the existing content-editor endpoints via
// contentEditorService. Client self-serve publish, gated on an explicit
// "I have previewed this post" confirmation (P1).
//
// `/business/:slug/blog/ai-writer`

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import useDirectUpload from '../hooks/useDirectUpload';
import { composeBlogPostStream } from '../services/blogAuthoringService';
import {
  listBlogPosts,
  readContentFile,
  writeBlogDraft,
  publishContent,
  splitFrontmatter,
  toVoiceExemplar,
  upsertFrontmatterField,
} from '../services/contentEditorService';
import { getSitePreviewUrls } from '../services/sitePreviewService';
import {
  listContentRefreshDrafts,
  getContentRefreshDraft,
  markContentRefreshPublished,
  dismissContentRefreshDraft,
} from '../services/contentRefreshService';

const SITE_SETTINGS_PATH = 'src/content/settings/site.md';
const MAX_EXEMPLARS = 3;

function errMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

// Light, dependency-free markdown preview. The TRUE render is the uat
// draft site (linked below); this is just a readable in-app outline so
// the client sees structure while iterating. Block-level only.
function renderPreviewBody(body) {
  const lines = String(body || '').split('\n');
  const out = [];
  let para = [];
  let list = [];
  const flushPara = (key) => {
    if (para.length) {
      out.push(
        <Typography key={key} variant="body1" sx={{ mb: 1.5 }}>
          {para.join(' ')}
        </Typography>,
      );
      para = [];
    }
  };
  const flushList = (key) => {
    if (list.length) {
      out.push(
        <Box component="ul" key={key} sx={{ pl: 3, mb: 1.5 }}>
          {list.map((li, i) => (
            <li key={i}>
              <Typography variant="body1" component="span">
                {li}
              </Typography>
            </li>
          ))}
        </Box>,
      );
      list = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^###\s+/.test(line)) {
      flushPara(`p${i}`);
      flushList(`l${i}`);
      out.push(
        <Typography key={`h3${i}`} variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
          {line.replace(/^###\s+/, '')}
        </Typography>,
      );
    } else if (/^##\s+/.test(line)) {
      flushPara(`p${i}`);
      flushList(`l${i}`);
      out.push(
        <Typography key={`h2${i}`} variant="h6" sx={{ mt: 2.5, mb: 1 }}>
          {line.replace(/^##\s+/, '')}
        </Typography>,
      );
    } else if (/^[-*]\s+/.test(line)) {
      flushPara(`p${i}`);
      list.push(line.replace(/^[-*]\s+/, ''));
    } else if (line === '') {
      flushPara(`p${i}`);
      flushList(`l${i}`);
    } else {
      flushList(`l${i}`);
      para.push(line);
    }
  });
  flushPara('pEnd');
  flushList('lEnd');
  return out;
}

export default function BlogAuthor({ embedded = false }) {
  const { business } = useContext(BusinessContext) || {};

  // Author-role gate is enforced server-side; this is a soft hint only.
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamReply, setStreamReply] = useState('');
  const [phase, setPhase] = useState(null);
  const [post, setPost] = useState(null); // { slug, frontmatter, markdown, suggestedPath }
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState('');

  // brand-voice grounding, loaded once
  const [exemplars, setExemplars] = useState([]);
  const [businessFacts] = useState([]);

  // save / publish state
  const [savedPath, setSavedPath] = useState('');
  const [savedSha, setSavedSha] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirtySinceSave, setDirtySinceSave] = useState(false);
  const [previewed, setPreviewed] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // cover image, uploaded through the shared Cloudinary media pipeline
  // (same path the AI site builder uses for brand assets). Stored in the
  // post's `coverImage` frontmatter as a `cld:` token.
  const { upload: uploadCover, uploading: coverUploading } = useDirectUpload();
  const [coverImage, setCoverImage] = useState(''); // cld: token
  const [coverImageUrl, setCoverImageUrl] = useState(''); // secure_url thumbnail
  const [coverError, setCoverError] = useState('');

  // uat/prod preview hosts, resolved the same way ContentEditor does.
  const [previewUrls, setPreviewUrls] = useState({ uatUrl: null, prodUrl: null });

  // Monthly content-refresh drafts (doc 28 W16, Full Service). Staged
  // server-side; "Review" loads one into this writer, and publishing it
  // counts it as one of the month's refreshes.
  const [refresh, setRefresh] = useState(null); // listContentRefreshDrafts()
  const [refreshDraftId, setRefreshDraftId] = useState('');
  const [refreshBusy, setRefreshBusy] = useState('');

  const existingSlugsRef = useRef(new Set());
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // Load brand-voice exemplars + the existing-slug set once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const posts = await listBlogPosts('uat');
        if (cancelled) return;
        existingSlugsRef.current = new Set(
          posts.map((p) => String(p.name || '').replace(/\.(md|mdx)$/i, '')),
        );
        const sample = posts.slice(0, MAX_EXEMPLARS);
        const loaded = [];
        for (const p of sample) {
          try {
            const file = await readContentFile(p.path, 'uat');
            loaded.push(toVoiceExemplar(file?.content || ''));
          } catch {
            // skip unreadable post; exemplars are best-effort
          }
        }
        if (!cancelled) setExemplars(loaded);
      } catch {
        // best-effort: proceed with no exemplars
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    listContentRefreshDrafts()
      .then((data) => {
        if (alive) setRefresh(data);
      })
      .catch(() => {
        // best-effort: no panel when the list does not load
      });
    return () => {
      alive = false;
    };
  }, []);

  const readyRefreshDrafts = (refresh?.drafts || []).filter(
    (d) => d.status === 'ready',
  );

  async function reviewRefreshDraft(id) {
    if (refreshBusy || streaming) return;
    setRefreshBusy(id);
    setError('');
    try {
      const draft = await getContentRefreshDraft(id);
      setPost({
        slug: draft.post.slug,
        frontmatter: draft.post.frontmatter || {},
        markdown: draft.post.markdown,
        suggestedPath: draft.post.suggestedPath,
      });
      setMessages([
        {
          role: 'assistant',
          content: `This month's draft answers "${draft.question}". Ask for any changes, then save it, preview it and publish.`,
        },
      ]);
      setWarnings(draft.warnings || []);
      setRefreshDraftId(id);
      setSavedPath('');
      setSavedSha('');
      setDirtySinceSave(true);
      setPreviewed(false);
      setPublished(false);
      setCoverImage('');
      setCoverImageUrl('');
    } catch (err) {
      setError(errMessage(err, 'Could not open that draft. Please retry.'));
    } finally {
      setRefreshBusy('');
    }
  }

  async function dismissRefreshDraft(id) {
    if (refreshBusy) return;
    setRefreshBusy(id);
    setError('');
    try {
      await dismissContentRefreshDraft(id);
      setRefresh((r) =>
        r ? { ...r, drafts: r.drafts.filter((d) => d.id !== id) } : r,
      );
      if (refreshDraftId === id) setRefreshDraftId('');
    } catch (err) {
      setError(errMessage(err, 'Could not dismiss that draft. Please retry.'));
    } finally {
      setRefreshBusy('');
    }
  }

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamReply]);

  // Resolve the tenant's uat preview host once, so "Preview on your site"
  // links to the real draft render (GET /v2/site-preview/urls).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const urls = await getSitePreviewUrls();
        if (alive) setPreviewUrls(urls);
      } catch {
        if (alive) setPreviewUrls({ uatUrl: null, prodUrl: null });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const siteProfile = {
    ...(business?.name ? { businessName: business.name } : {}),
    ...(business?.businessType ? { businessType: business.businessType } : {}),
  };

  const previewBody = post ? splitFrontmatter(post.markdown).body : '';

  // The draft renders on the tenant's uat site. Link straight to the post
  // when the uat host resolves; otherwise show the path.
  const previewUrl =
    post && previewUrls.uatUrl
      ? `${previewUrls.uatUrl.replace(/\/$/, '')}/blog/${post.slug}/`
      : '';

  async function handleCoverFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setCoverError('');
    try {
      const r = await uploadCover(file);
      setCoverImage(r.token); // cld:<public_id>
      setCoverImageUrl(r.secure_url);
      setDirtySinceSave(true);
      setPreviewed(false);
    } catch {
      setCoverError('That image did not upload. Try a JPG or PNG under 10 MB.');
    }
  }

  function uniqueSlug(slug) {
    const set = existingSlugsRef.current;
    if (!set.has(slug)) return slug;
    let n = 2;
    while (set.has(`${slug}-${n}`)) n += 1;
    return `${slug}-${n}`;
  }

  const send = useCallback(
    async (text) => {
      const trimmed = String(text || '').trim();
      if (!trimmed || streaming) return;
      setError('');
      const nextMessages = [...messages, { role: 'user', content: trimmed }];
      setMessages(nextMessages);
      setInput('');
      setStreaming(true);
      setStreamReply('');
      setPhase(null);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const result = await composeBlogPostStream(
          {
            messages: nextMessages,
            siteProfile: Object.keys(siteProfile).length ? siteProfile : undefined,
            voiceExemplars: exemplars.length ? exemplars : undefined,
            businessFacts: businessFacts.length ? businessFacts : undefined,
            currentPost: post
              ? { frontmatter: post.frontmatter, body: previewBody }
              : undefined,
          },
          {
            onMessage: (t) => setStreamReply(t),
            onPhase: (p) => setPhase(p),
            onProgress: () => {},
            signal: controller.signal,
          },
        );
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: result.message || 'Here is your draft.' },
        ]);
        setPost(result.post);
        setWarnings(result.warnings || []);
        setDirtySinceSave(true);
        setPreviewed(false);
      } catch (err) {
        setError(errMessage(err, 'The AI writer hit a snag. Please retry.'));
      } finally {
        setStreaming(false);
        setStreamReply('');
        setPhase(null);
        abortRef.current = null;
      }
    },
    [messages, streaming, exemplars, post, previewBody],
  );

  async function handleSaveDraft() {
    if (!post || saving) return;
    setSaving(true);
    setError('');
    try {
      // First save: dedupe the slug against existing posts and create a
      // new file. Subsequent saves update the same file (sha).
      let slug = post.slug;
      let sha = savedSha;
      if (!savedPath) {
        slug = uniqueSlug(post.slug);
      } else {
        slug = savedPath.replace(/^.*\/(.+)\.mdx?$/, '$1');
      }
      // The AI writes coverImageAlt but not the image itself; inject the
      // uploaded cover token into the post's `coverImage` frontmatter.
      const content = coverImage
        ? upsertFrontmatterField(post.markdown, 'coverImage', coverImage)
        : post.markdown;
      const res = await writeBlogDraft({ slug, content, sha });
      const path = `src/content/blog/${slug}.md`;
      existingSlugsRef.current.add(slug);
      setSavedPath(path);
      setSavedSha(res?.contentSha || '');
      setPost((p) => (p ? { ...p, slug, suggestedPath: path } : p));
      setDirtySinceSave(false);
    } catch (err) {
      setError(errMessage(err, 'Could not save the draft. Please retry.'));
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    setConfirmOpen(false);
    if (!savedPath || publishing) return;
    setPublishing(true);
    setError('');
    try {
      await publishContent();
      setPublished(true);
      if (refreshDraftId) {
        const id = refreshDraftId;
        try {
          await markContentRefreshPublished(id, savedPath);
          setRefresh((r) =>
            r
              ? {
                  ...r,
                  publishedThisMonth: (r.publishedThisMonth || 0) + 1,
                  drafts: r.drafts.filter((d) => d.id !== id),
                }
              : r,
          );
        } catch {
          // The post is live; the count is retried from the list on reload.
        }
        setRefreshDraftId('');
      }
    } catch (err) {
      setError(errMessage(err, 'Could not publish. Please retry.'));
    } finally {
      setPublishing(false);
    }
  }

  const canSave = !!post && !streaming && (dirtySinceSave || !savedPath);
  const canPublish =
    !!savedPath && !dirtySinceSave && previewed && !publishing && !published;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pt: embedded ? 0 : undefined, height: '100%' }}>
      {!embedded ? (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <AutoAwesomeRoundedIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            AI Blog Writer
          </Typography>
        </Stack>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Describe the post you want. The writer will draft it in your site&apos;s
        voice. Save it as a draft, preview it on your site, then publish.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {refresh?.enabled && readyRefreshDrafts.length ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Your monthly drafts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Your plan includes {refresh.allowance} content refreshes a month. We
            wrote these to answer questions your customers ask AI assistants.
            Published this month: {refresh.publishedThisMonth}.
          </Typography>
          <Stack spacing={1} divider={<Divider flexItem />}>
            {readyRefreshDrafts.map((d) => (
              <Stack
                key={d.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {d.title || 'Untitled draft'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Answers: {d.question}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant={refreshDraftId === d.id ? 'contained' : 'outlined'}
                    disabled={!!refreshBusy || streaming}
                    startIcon={
                      refreshBusy === d.id ? <CircularProgress size={14} /> : null
                    }
                    onClick={() => reviewRefreshDraft(d.id)}
                  >
                    {refreshDraftId === d.id ? 'Reviewing' : 'Review'}
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={!!refreshBusy}
                    onClick={() => dismissRefreshDraft(d.id)}
                  >
                    Dismiss
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Paper>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          minHeight: 0,
        }}
      >
        {/* Conversation pane */}
        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: 560 }}>
          <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {messages.length === 0 && !streaming ? (
              <Typography variant="body2" color="text.secondary">
                {'Try: "Write a post about why regular gutter cleaning prevents roof damage, for homeowners, with a call to book a service."'}
              </Typography>
            ) : null}
            <Stack spacing={1.5}>
              {messages.map((m, i) => (
                <Box
                  key={i}
                  sx={{
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                    color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {m.content}
                  </Typography>
                </Box>
              ))}
              {streaming ? (
                <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      {phase === 'repairing'
                        ? 'Fixing up the draft...'
                        : streamReply
                          ? 'Writing...'
                          : 'Thinking...'}
                    </Typography>
                  </Stack>
                  {streamReply ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {streamReply}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Stack>
          </Box>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={4}
                placeholder="Describe the post, or ask for a change..."
                value={input}
                disabled={streaming}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send(input);
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={() => send(input)}
                disabled={streaming || !input.trim()}
                sx={{ minWidth: 44, px: 0 }}
                aria-label="Send"
              >
                <SendRoundedIcon fontSize="small" />
              </Button>
            </Stack>
          </Box>
        </Paper>

        {/* Preview + actions pane */}
        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: 560 }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {!post ? (
              <Typography variant="body2" color="text.secondary">
                Your draft will appear here.
              </Typography>
            ) : (
              <>
                {coverImageUrl ? (
                  <Box
                    component="img"
                    src={coverImageUrl}
                    alt={post.frontmatter?.coverImageAlt || ''}
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'cover',
                      borderRadius: 1,
                      mb: 1.5,
                    }}
                  />
                ) : null}
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {post.frontmatter?.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {post.frontmatter?.description}
                </Typography>
                {post.frontmatter?.tags?.length ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    {post.frontmatter.tags.map((t) => (
                      <Chip key={t} label={t} size="small" />
                    ))}
                  </Stack>
                ) : null}
                <Divider sx={{ mb: 1.5 }} />
                {renderPreviewBody(previewBody)}
              </>
            )}
          </Box>

          {warnings.length ? (
            <Alert severity="info" sx={{ mx: 1.5, mb: 1 }}>
              <Stack spacing={0.25}>
                {warnings.map((w, i) => (
                  <Typography variant="caption" key={i}>
                    {w}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : null}

          <Divider />
          <Box sx={{ p: 1.5 }}>
            {published ? (
              <Alert severity="success">
                Published. Your post is going live on your site now.
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {coverImageUrl ? (
                    <Box
                      component="img"
                      src={coverImageUrl}
                      alt="Cover preview"
                      sx={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 1 }}
                    />
                  ) : null}
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={
                      coverUploading ? <CircularProgress size={14} /> : <ImageOutlinedIcon />
                    }
                    disabled={coverUploading}
                  >
                    {coverImage ? 'Change cover image' : 'Add cover image'}
                    <input type="file" hidden accept="image/*" onChange={handleCoverFile} />
                  </Button>
                  {coverImage ? (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => {
                        setCoverImage('');
                        setCoverImageUrl('');
                        setDirtySinceSave(true);
                        setPreviewed(false);
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Stack>
                {coverError ? (
                  <Typography variant="caption" color="error">
                    {coverError}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={saving ? <CircularProgress size={14} /> : <SaveOutlinedIcon />}
                    disabled={!canSave || saving}
                    onClick={handleSaveDraft}
                  >
                    {savedPath ? 'Save changes' : 'Save draft'}
                  </Button>
                  {savedPath ? (
                    previewUrl ? (
                      <Button
                        variant="text"
                        size="small"
                        endIcon={<OpenInNewRoundedIcon />}
                        component="a"
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Preview on your site
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        Saved as draft at /blog/{post?.slug}/ on your preview site.
                      </Typography>
                    )
                  ) : null}
                </Stack>

                {savedPath ? (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={previewed}
                          disabled={dirtySinceSave}
                          onChange={(e) => setPreviewed(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          I have previewed this post and it is ready to publish.
                        </Typography>
                      }
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        publishing ? <CircularProgress size={14} /> : <PublishRoundedIcon />
                      }
                      disabled={!canPublish}
                      onClick={() => setConfirmOpen(true)}
                    >
                      Approve and publish
                    </Button>
                    {dirtySinceSave ? (
                      <Typography variant="caption" color="text.secondary">
                        You have unsaved changes. Save them before publishing.
                      </Typography>
                    ) : null}
                  </>
                ) : null}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>

      {streaming ? <LinearProgress sx={{ mt: 2 }} /> : null}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Publish this post?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This makes &quot;{post?.frontmatter?.title}&quot; live on your public website
            and visible to search engines. You can edit or remove it later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePublish}>
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
